/**
 * @fileoverview Main Games component that serves as a hub for various educational games.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaRandom,
  FaBuffer,
  FaClock,
  FaKeyboard,
  FaSortNumericUp,
  FaFlagCheckered,
  FaQuestionCircle,
  FaTv,
  IconType,
} from "react-icons/fa";
import { tourStepsGames } from "./tourStepsGames";
import GuidedTour from "../../components/GuidedTour";
import BubbleText from "../../components/BubbleText";

interface GameLink {
  to: string;
  className: string;
  icon: IconType;
  color: string;
  title: string;
  description: string;
}

const games: GameLink[] = [
  {
    to: "/games/matching-game",
    className: "matching-game",
    icon: FaRandom,
    color: "#4A90E2",
    title: "Matching Game",
    description: "Drag and connect words to their corresponding cards!",
  },
  {
    to: "/games/multiple-words-game",
    className: "multiple-words-game",
    icon: FaBuffer,
    color: "#E94E77",
    title: "Multiple Words Game",
    description: "Match related words, like Furniture with Couch!",
  },
  {
    to: "/games/timed-challenges",
    className: "timed-challenges-game",
    icon: FaClock,
    color: "#F5A623",
    title: "Timed Challenges",
    description: "See how many items you can correctly identify in 60 seconds!",
  },
  {
    to: "/games/typing-simulator",
    className: "typing-simulator-game",
    icon: FaKeyboard,
    color: "#7B68EE",
    title: "Typing Simulator",
    description: "Read the passage on the screen and type it as you go!",
  },
  {
    to: "/games/missing-number",
    className: "missing-number-game",
    icon: FaSortNumericUp,
    color: "#1ABC9C",
    title: "Missing Number",
    description: "Fill in the blank in a number sequence, like 1, 2, __, 4!",
  },
  {
    to: "/games/timed-trials",
    className: "timed-trials-game",
    icon: FaFlagCheckered,
    color: "#E74C3C",
    title: "Timed Trials",
    description: "Take turns and see who can finish a collection the fastest!",
  },
  {
    to: "/games/multiple-choice",
    className: "multiple-choice-game",
    icon: FaQuestionCircle,
    color: "#9B59B6",
    title: "Multiple Choice",
    description:
      "Click a colored button for the right answer. The choices shuffle each time!",
  },
  {
    to: "/games/quiz-show",
    className: "quiz-show-game",
    icon: FaTv,
    color: "#16A085",
    title: "Quiz Show",
    description:
      "Questions on the class screen. Students answer on their phones!",
  },
];

const Games: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  useEffect(() => {
    const loadData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted", "true");
  };

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  return (
    <div className="page-container text-theme-text">
      <h1 className="inherit mb-8 text-3xl font-bold">
        <BubbleText>Games</BubbleText>
      </h1>
      <p className="games-intro inherit">
        Welcome to the Games page! Here are some fun activities to enhance
        engagement.
      </p>
      {isLoading ? (
        <p className="inherit">Loading games...</p>
      ) : (
        <div className="grid w-full max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2">
          {games.map((game) => {
            const Icon = game.icon;
            return (
              <Link
                key={game.to}
                to={game.to}
                className={`${game.className} flex min-h-[88px] flex-col gap-2 rounded-lg border border-black p-4 transition hover:bg-black/5 active:opacity-70 sm:flex-row sm:items-start sm:gap-3`}
              >
                <Icon
                  className="shrink-0 text-2xl"
                  style={{ color: game.color }}
                  aria-hidden
                />
                <div className="min-w-0">
                  <h2 className="inherit text-xl font-semibold sm:text-2xl">
                    {game.title}
                  </h2>
                  <p className="inherit text-sm leading-relaxed sm:text-base">
                    {game.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <GuidedTour
        steps={tourStepsGames()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={handleTourStepChange}
        tourName="games"
      />
    </div>
  );
};

export default Games;
